// urql client with three pipeline exchanges:
//   authExchange   — injects bearer + retries once on 401 after a /refresh.
//   errorExchange  — normalises CombinedError into our ApiError shape and
//                    surfaces a single notification path.
//   retryExchange  — exponential backoff for transient network errors.
//
// The schema-codegen output lands at src/gql/ during F1.5 (codegen wiring);
// until then this client is generic and works with hand-written `gql`
// documents.

import { authExchange } from "@urql/exchange-auth";
import { retryExchange } from "@urql/exchange-retry";
import {
  Client,
  cacheExchange,
  fetchExchange,
  mapExchange,
  type CombinedError,
} from "urql";

import { config } from "@/lib/config";
import { authClient, isLoggedOut } from "@/lib/auth/authClient";
import { tokenStore } from "@/lib/auth/tokenStore";

// Idle CombinedError → our typed shape so consumers don't depend on urql.
function asApiError(error: CombinedError) {
  const status = (error.response as Response | undefined)?.status ?? 0;
  return {
    code: status === 401 ? "unauthorized" : status === 403 ? "forbidden" : "unknown",
    message: error.message,
    status,
    detail: error,
  } as const;
}

export function createGraphqlClient(): Client {
  return new Client({
    url: config.graphqlEndpoint,
    fetchOptions: () => ({
      // HTTP-only refresh cookie travels here too.
      credentials: "include",
    }),
    exchanges: [
      cacheExchange,
      mapExchange({
        onError(error) {
          // Centralised place to forward to Sentry / toast bus later.
          // eslint-disable-next-line no-console
          console.warn("gql_error", asApiError(error));
        },
      }),
      retryExchange({
        initialDelayMs: 500,
        maxDelayMs: 8000,
        randomDelay: true,
        maxNumberAttempts: 3,
        retryIf: (error) => {
          const status = (error.response as Response | undefined)?.status ?? 0;
          // Don't retry auth, validation, or 5xx-with-deliberate-stale.
          if (status === 401 || status === 403 || status === 422) return false;
          return Boolean(error.networkError) || status >= 500;
        },
      }),
      authExchange(async (utils) => {
        return {
          addAuthToOperation(operation) {
            const token = tokenStore.accessToken;
            if (!token) return operation;
            return utils.appendHeaders(operation, { Authorization: `Bearer ${token}` });
          },
          didAuthError(error) {
            return error.graphQLErrors.some((e) => e.extensions?.code === "UNAUTHENTICATED") ||
              (error.response as Response | undefined)?.status === 401;
          },
          async refreshAuth() {
            try {
              const result = await authClient.refresh();
              if (isLoggedOut(result)) {
                tokenStore.clear();
                // Expected logged-out state (no/expired refresh cookie). Hand
                // off to the auth provider — no thrown error, no console noise.
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("powerbess:auth-expired"));
                }
                return;
              }
              tokenStore.set(result.tokens.access_token);
            } catch {
              tokenStore.clear();
              // A real refresh failure — let the auth provider decide whether
              // to redirect to /login or surface the error.
              if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("powerbess:auth-expired"));
              }
            }
          },
          willAuthError() {
            // Force a refresh when the access token is within 30s of expiry.
            const remaining = tokenStore.expiresInSeconds();
            return remaining !== null && remaining < 30;
          },
        };
      }),
      fetchExchange,
    ],
  });
}
